import classNames from 'classnames';
import DOMPurify from 'dompurify';
import { useRef } from 'react';

import { useLogoCarousel } from '@hooks/useLogoCarousel';
import { CoffeeIcon } from '@icons';
import { setupForDark } from '@theme';
import { GoalSubscriptionStatus } from 'lib/models/shared';

interface GoalLogoProps {
  logoSvg?: string;
  size: number;
  status?: GoalSubscriptionStatus;
  animateCarousel?: boolean;
}

// FIXME: because of security reasons we should store the logo as optimized PNG somewhere at the BACKEND
export default function GoalLogo({ logoSvg, size, status, animateCarousel }: GoalLogoProps) {
  const carouselRef = useRef<HTMLDivElement>(null);
  useLogoCarousel(carouselRef, !!animateCarousel && status !== GoalSubscriptionStatus.PAUSED);

  // Show CoffeeIcon if status is PAUSED
  if (status === GoalSubscriptionStatus.PAUSED) {
    return (
      <div
        className={classNames('flex-shrink-0', 'relative', setupForDark('text-icon-secondary'))}
        style={{
          width: size,
          height: size
        }}
      >
        <CoffeeIcon size={size} />
      </div>
    );
  }

  // Fallback to placeholder if no logo
  if (!logoSvg) {
    return (
      <div className='relative shrink-0' style={{ width: size, height: size }}>
        <div className='absolute inset-0 grid grid-cols-2'>
          <div className='bg-[#FF7236]' />
          <div className='bg-[#E04E4E]' />
          <div className='bg-[#FFBB53]' />
          <div className='bg-[#3E5ABB]' />
        </div>
      </div>
    );
  }

  // Render sanitized SVG
  let svg = logoSvg
    .replace('width="272"', `width="${size}"`)
    .replace('height="272"', `height="${size}"`);

  const sanitizedSvg = DOMPurify.sanitize(svg, { USE_PROFILES: { svg: true, svgFilters: true } });

  return (
    <div
      className={classNames('flex-shrink-0', 'relative')}
      style={{
        width: size,
        height: size
      }}
      data-logo-carousel={animateCarousel ? '' : undefined}
    >
      <div
        ref={carouselRef}
        className='top-0 left-0'
        dangerouslySetInnerHTML={{ __html: sanitizedSvg }}
      />
    </div>
  );
}
